import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, TrendingDown, Smile, Heart, Check, Clock, MessageSquare, Paperclip, Plus, ClipboardList, Megaphone, ArrowRight } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { ENGAGEMENT_DATA, ENGAGEMENT_CHAT_RESPONSES } from '../../data/engagementData';

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const KudosCard = ({ kudos, isDark, onAction }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-lg px-3 py-2.5`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Heart className={`w-3 h-3 ${isDark ? 'text-pink-400' : 'text-pink-500'}`} />
          <span className={`text-[11px] font-semibold ${tp}`}>{kudos.from}</span>
          <span className={`text-[9px] ${ts}`}>to</span>
          <span className={`text-[11px] font-semibold ${tp}`}>{kudos.to}</span>
        </div>
        <span className={`text-[8px] ${ts}`}>{kudos.time}</span>
      </div>
      <p className={`text-[10px] leading-relaxed ${ts} italic`}>"{kudos.message}"</p>
      <div className="flex items-center justify-between mt-1.5">
        <span className={`text-[8px] font-medium inline-block px-1.5 py-0.5 rounded-full ${isDark ? 'bg-pink-500/10 text-pink-400' : 'bg-pink-50 text-pink-600'}`}>{kudos.value}</span>
        <button onClick={() => onAction?.('Amplify Recognition', `${kudos.from}'s kudos to ${kudos.to}`)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
          <Megaphone className="w-3 h-3" /> Amplify
        </button>
      </div>
    </div>
  );
};

const ActionItem = ({ item, isDark, onAction }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const statusStyles = {
    'done': { icon: Check, color: isDark ? 'text-emerald-400' : 'text-emerald-600', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
    'in-progress': { icon: Clock, color: isDark ? 'text-indigo-400' : 'text-indigo-600', bg: isDark ? 'bg-indigo-500/10' : 'bg-indigo-50' },
    'open': { icon: AlertTriangle, color: isDark ? 'text-amber-400' : 'text-amber-600', bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50' },
  };
  const s = statusStyles[item.status] || statusStyles.open;
  const Icon = s.icon;
  const ctaLabel = item.status === 'done' ? null : item.status === 'in-progress' ? 'Follow Up' : 'Start';
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${s.bg}`}>
        <Icon className={`w-3 h-3 ${s.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[10px] leading-relaxed ${tp}`}>{item.action}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-[8px] ${ts}`}>{item.owner}</span>
          <span className={`text-[8px] ${ts}`}>Due: {item.dueDate}</span>
        </div>
        <p className={`text-[8px] mt-0.5 ${isDark ? 'text-white/25' : 'text-slate-500'}`}>{item.source}</p>
      </div>
      {ctaLabel && (
        <button onClick={() => onAction?.(`${ctaLabel} Action Item`, item.action)} className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-[8px] font-medium mt-0.5 transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
          <ArrowRight className="w-2.5 h-2.5" /> {ctaLabel}
        </button>
      )}
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Tali · {item.time}</p>
      </div>
    </div>
  );
};

const V4PeopleEngagement = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || ENGAGEMENT_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCta = (label, detail) => {
    sendMessage(`${label} — ${detail}`, { text: `I'll take care of "${label}" for ${detail}. Setting that up now and will confirm once ready.\n\nNeed anything else?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Tali about engagement..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="engagement-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.tali} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Tali</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="engagement-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="engagement-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="engagement-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Engagement</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.tali} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="engagement-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Smile className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Engagement</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleCta('Create Pulse Survey', 'New quick pulse check for all teams')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="engagement-create-survey-btn">
              <ClipboardList className="w-3.5 h-3.5" /> Create Survey
            </button>
            <button onClick={() => handleCta('Give Kudos', 'Draft a new recognition message')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="engagement-give-kudos-btn">
              <Heart className="w-3.5 h-3.5" /> Give Kudos
            </button>
          </div>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        <div className="grid grid-cols-6 gap-3">
          {ENGAGEMENT_DATA.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Department Pulse — compact grid */}
        <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl overflow-hidden`}>
          <div className="flex items-center gap-2 px-4 py-2">
            <MessageSquare className={`w-3.5 h-3.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-[11px] font-semibold ${tp}`}>Department Pulse</p>
          </div>
          <div className={`grid grid-cols-[2fr_1fr_1.3fr_1fr_0.8fr_2fr] items-center px-4 py-1.5 border-t border-b ${border}`}>
            <span className={`text-[9px] font-semibold ${ts}`}>Department</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>Score</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>Participation</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>Satisfaction</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>Trend</span>
            <span className={`text-[9px] font-semibold text-right ${ts}`}>Flag</span>
          </div>
          {ENGAGEMENT_DATA.departmentPulse.map(dept => {
            const engColor = dept.engagement >= 8 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : dept.engagement >= 7.5 ? tp : (isDark ? 'text-amber-400' : 'text-amber-500');
            const trendIcon = dept.trend === 'up' ? <TrendingUp className="w-3 h-3 text-emerald-400" /> : dept.trend === 'down' ? <TrendingDown className="w-3 h-3 text-red-400" /> : <span className={`text-[9px] ${ts}`}>—</span>;
            return (
              <div key={dept.department} className={`grid grid-cols-[2fr_1fr_1.3fr_1fr_0.8fr_2fr] items-center px-4 py-2 border-b ${border} last:border-0`}>
                <span className={`text-[11px] font-medium ${tp}`}>{dept.department}</span>
                <span className={`text-[10px] font-bold text-center ${engColor}`}>{dept.engagement}</span>
                <div className="px-1">
                  <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`}>
                    <div className="h-full rounded-full bg-indigo-500/40 transition-all" style={{ width: `${dept.participation}%` }} />
                  </div>
                  <span className={`text-[8px] ${ts}`}>{dept.participation}%</span>
                </div>
                <span className={`text-[10px] text-center ${tp}`}>{dept.satisfaction}</span>
                <div className="flex justify-center">{trendIcon}</div>
                <div className="flex items-center justify-end gap-2">
                  <span className={`text-[9px] text-right ${dept.flag ? (isDark ? 'text-amber-400/80' : 'text-amber-600') : ts}`}>{dept.flag || '—'}</span>
                  {dept.flag && (
                    <button onClick={() => handleCta('Investigate Flag', `${dept.department}: ${dept.flag}`)} className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}>
                      <ArrowRight className="w-3 h-3" /> Investigate
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Two columns: Kudos + Actions */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Heart className={`w-4 h-4 ${isDark ? 'text-pink-400' : 'text-pink-500'}`} />
                <p className={`text-xs font-semibold ${tp}`}>Recognition Feed</p>
                <span className={`text-[9px] ${ts}`}>{ENGAGEMENT_DATA.recognitionFeed.length} this week</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {ENGAGEMENT_DATA.recognitionFeed.slice(0, 4).map(k => <KudosCard key={k.id} kudos={k} isDark={isDark} onAction={handleCta} />)}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                <p className={`text-xs font-semibold ${tp}`}>Survey Action Items</p>
                <span className={`text-[9px] ${ts}`}>{ENGAGEMENT_DATA.surveyActions.filter(a => a.status !== 'done').length} open</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {ENGAGEMENT_DATA.surveyActions.map(a => <ActionItem key={a.id} item={a} isDark={isDark} onAction={handleCta} />)}
            </div>
          </div>
        </div>

        {/* Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.tali} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Tali's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {ENGAGEMENT_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4PeopleEngagement;
